/**
 * MemoryStorage.ts - MemoryStorage - In-memory storage adapter
 * 
 * CHANGE HISTORY:
 * - 251021_0517 [8786ca9] danielbeer: feat: apply authr pattern to all 43 remaining packages
 * - 251021_0447 [ce7b5e6] danielbeer: feat: automate TypeScript and Actr integration for all packages
 * - 251021_0421 [7529d5e] danielbeer: feat: migrate all packages to TypeScript structure
 */
import { StorageAdapter } from './StorageAdapter';

export class MemoryStorage extends StorageAdapter {
  data: Map<string, Map<string, any>>;
  initialized: boolean;

  constructor() {
    super();
    this.data = new Map(); // entityType -> Map(id -> data)
    this.initialized = false;
  }

  /**
   * Initialize storage
   */
  async initialize() {
    if (this.initialized) return;
    this.data = new Map();
    this.initialized = true;
  }

  /**
   * Get storage for entity type
   * @param {string} entityType
   * @returns {Map}
   */
  _getStorage(entityType: string): Map<string, any> {
    if (!this.data.has(entityType)) {
      this.data.set(entityType, new Map());
    }
    return this.data.get(entityType)!;
  }

  /**
   * Store an entity
   */
  async set(entityType: string, id: string, data) {
    const storage = this._getStorage(entityType);
    const entity = {
      ...data,
      id,
      _type: entityType,
      _createdAt: data._createdAt || Date.now(),
      _updatedAt: Date.now()
    };
    storage.set(id, entity);
    return entity;
  }

  /**
   * Retrieve an entity
   */
  async get(entityType: string, id: string) {
    const storage = this._getStorage(entityType);
    return storage.get(id) || null;
  }

  /**
   * Delete an entity
   */
  async delete(entityType: string, id: string) {
    const storage = this._getStorage(entityType);
    return storage.delete(id);
  }

  /**
   * Query entities
   */
  async query(entityType: string, filters: Record<string, any> = {}) {
    const storage = this._getStorage(entityType);
    const entities = Array.from(storage.values());

    // Apply filters
    return entities.filter(entity => {
      return Object.entries(filters).every(([key, value]) => {
        if (typeof value === 'function') {
          return value(entity[key]);
        }
        return entity[key] === value;
      });
    });
  }

  /**
   * Get all entities of a type
   */
  async getAll(entityType: string) {
    const storage = this._getStorage(entityType);
    return Array.from(storage.values());
  }

  /**
   * Clear all data
   */
  async clear() {
    this.data.clear();
  }

  /**
   * Get statistics
   */
  getStats(): Record<string, number> {
    const stats: Record<string, number> = {};
    for (const [entityType, storage] of this.data.entries()) {
      stats[entityType] = storage.size;
    }
    return stats;
  }
}

export default MemoryStorage;
